import { useState, useMemo } from 'react';
import { useSubscriptionStatus } from './useSubscriptionStatus';
import { useQueryClient } from '@tanstack/react-query';

interface ExceededLimit {
	name: string;
	current: number;
	limit: number;
	percentage: number;
}

interface TrialExpirationModalState {
	shouldShow: boolean;
	daysRemaining: number | null;
	exceededLimits: ExceededLimit[];
	hasExceededLimits: boolean;
}

const TRIAL_EXPIRATION_NOTIFICATION_DAYS = [3, 1]; // Show modal 3 days and 1 day before expiration
const STORAGE_KEY_PREFIX = 'trial_expiration_modal_shown_';

/**
 * Hook to manage trial expiration modal display logic
 * Shows modal 3 days and 1 day before trial expiration
 * Also checks for exceeded data limits
 */
export const useTrialExpirationModal = (): TrialExpirationModalState & {
	onClose: () => void;
	onSubscribe: () => void;
} => {
	const subscription = useSubscriptionStatus();
	const queryClient = useQueryClient();
	const [hasShownForCurrentPeriod, setHasShownForCurrentPeriod] =
		useState(false);

	// Get usage stats to check for exceeded limits
	const statCountData = queryClient.getQueryData(['statCount']) as any;
	const limits = statCountData?.limits || {};
	const usageData = {
		data: statCountData?.data || 0,
		models: statCountData?.models || 0,
		projects: statCountData?.projects || 0,
		storage: statCountData?.storage || 0,
		users: statCountData?.users || 0,
		aiCall: statCountData?.aiCall || 0,
	};

	// Check if user is in trial
	const isInTrial = useMemo(() => {
		if (!subscription.currentSubscription) return false;

		const status = subscription.currentSubscription.status;
		const endDate = subscription.endDate;

		// Explicitly check for trialing status
		if (status === 'trialing') {
			if (endDate) {
				const now = new Date();
				const trialEnd = new Date(endDate);
				// Only consider it a trial if endDate is in the future
				return trialEnd > now;
			}
			return true; // If status is trialing but no endDate, assume it's a trial
		}

		// Also check raw billing info for trialing status
		const rawBilling = subscription.rawBillingInfo;
		if (rawBilling && typeof rawBilling === 'object') {
			// Check if any subscription in billing_info has trialing status
			const billingValues = Array.isArray(rawBilling)
				? rawBilling
				: Object.values(rawBilling);

			const hasTrialingStatus = billingValues.some((billing: any) => {
				if (billing?.status === 'trialing' && billing?.isActive) {
					if (billing?.endDate) {
						const trialEnd = new Date(billing.endDate);
						return trialEnd > new Date();
					}
					return true;
				}
				return false;
			});

			if (hasTrialingStatus) return true;
		}

		return false;
	}, [
		subscription.currentSubscription,
		subscription.endDate,
		subscription.rawBillingInfo,
	]);

	// Calculate days remaining until trial expiration
	const daysRemaining = useMemo(() => {
		if (!isInTrial || !subscription.endDate) return null;

		const now = new Date();
		const trialEnd = new Date(subscription.endDate);
		const diffTime = trialEnd.getTime() - now.getTime();
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

		return diffDays > 0 ? diffDays : 0;
	}, [isInTrial, subscription.endDate]);

	// Check which limits are exceeded
	const exceededLimits = useMemo(() => {
		const exceeded: ExceededLimit[] = [];

		const limitMap = {
			data: {
				name: 'Data Items',
				current: usageData.data,
				limit: limits.data_limit || 300,
			},
			models: {
				name: 'Data Models',
				current: usageData.models,
				limit: limits.data_folder_limit || 3,
			},
			projects: {
				name: 'Projects',
				current: usageData.projects,
				limit: limits.project_limit || 1,
			},
			storage: {
				name: 'Storage (MB)',
				current: usageData.storage,
				limit: limits.storage_limit || 300,
			},
			users: {
				name: 'Users',
				current: usageData.users,
				limit: limits.max_users || 3,
			},
			aiCall: {
				name: 'AI Calls',
				current: usageData.aiCall,
				limit: limits.ai_call_limit || 100,
			},
		};

		Object.entries(limitMap).forEach(([key, { name, current, limit }]) => {
			if (
				limit &&
				limit !== '∞' &&
				typeof limit === 'number' &&
				limit > 0
			) {
				if (current >= limit) {
					exceeded.push({
						name,
						current,
						limit,
						percentage: Math.min(
							100,
							Math.round((current / limit) * 100)
						),
					});
				}
			}
		});

		return exceeded;
	}, [usageData, limits]);

	// Determine if modal should be shown
	const shouldShow = useMemo(() => {
		// Only show if user is in trial
		if (!isInTrial || daysRemaining === null) return false;

		// Check if we should show for this day (3 days or 1 day before expiration)
		const shouldShowForDay =
			TRIAL_EXPIRATION_NOTIFICATION_DAYS.includes(daysRemaining);

		if (!shouldShowForDay) return false;

		// Check if we've already shown the modal for this specific day
		// Use try-catch for environments where localStorage might not be available
		try {
			if (typeof window !== 'undefined' && window.localStorage) {
				const storageKey = `${STORAGE_KEY_PREFIX}${daysRemaining}_${subscription.endDate?.getTime()}`;
				const hasShown = localStorage.getItem(storageKey) === 'true';
				return !hasShown;
			}
		} catch (e) {
			// If localStorage is not available, show the modal anyway
			console.warn(
				'localStorage not available, showing trial expiration modal'
			);
		}

		return true;
	}, [isInTrial, daysRemaining, subscription.endDate]);

	const onClose = () => {
		if (daysRemaining !== null) {
			try {
				if (typeof window !== 'undefined' && window.localStorage) {
					const storageKey = `${STORAGE_KEY_PREFIX}${daysRemaining}_${subscription.endDate?.getTime()}`;
					localStorage.setItem(storageKey, 'true');
					setHasShownForCurrentPeriod(true);
				}
			} catch (e) {
				console.warn(
					'Failed to save trial modal state to localStorage:',
					e
				);
			}
		}
	};

	const onSubscribe = () => {
		// Navigate to upgrade/subscription screen
		// This will be handled by the parent component
		onClose();
	};

	return {
		shouldShow,
		daysRemaining,
		exceededLimits,
		hasExceededLimits: exceededLimits.length > 0,
		onClose,
		onSubscribe,
	};
};

export default useTrialExpirationModal;
